import React, { MutableRefObject } from "react";
import { Box, Button } from "@mui/material";

import { PageManagementDragAndDrop, TreeData } from "@/features/admin/components";
import {
  Modal,
  ModalActions,
  ModalComponent,
  ModalContent,
  ModalContentText,
  showToast,
} from "@/features/shared/components";
import { Widget } from "@/lib/api";
import { usePageData } from "@/features/admin/features/pages-management";
import { useReorderWidgetsMutation } from "@/features/admin/features/widgets";
import { useTranslation } from "@/lib/i18n";

import Item from "./item";
import { treeDataFromTabs } from "./helpers";

type Props = {
  pageId: string;
  onClose?: () => void;
  containerId: string;
};

const WidgetOrderDialog: React.ForwardRefRenderFunction<ModalComponent, Props> = (
  { containerId, onClose },
  ref,
) => {
  const { t } = useTranslation();
  const reorderWidgetsMutation = useReorderWidgetsMutation();
  const { page } = usePageData();

  const modalRef = ref as MutableRefObject<ModalComponent>;

  const widgets = React.useMemo(
    () =>
      page?.widget_containers.find((container) => container.id === containerId)?.widgets ??
      [],
    [page?.widget_containers, containerId],
  );

  const [tabs, setTabs] = React.useState<TreeData<Widget>>(treeDataFromTabs(widgets));

  const handleClose = () => {
    modalRef.current?.close?.();
  };

  async function handleSave() {
    reorderWidgetsMutation.mutate(
      {
        pageId: page?.id ?? "",
        widgetId: containerId,
        data: tabs.map((tab) => String(tab.id)),
      },
      {
        onSuccess: () => {
          showToast(t("admin.messages.widget_order_updated"));
          modalRef.current?.close();
        },
      },
    );
  }

  return (
    <Modal onClose={onClose} ref={ref}>
      <ModalContent>
        <ModalContentText>
          {t("admin.pages.widget_order_dialog.description")}
        </ModalContentText>
        <Box sx={{ mt: 4 }}>
          <PageManagementDragAndDrop<Widget>
            treeData={tabs}
            dragAndDropEnabled
            onChange={(newOrder) => setTabs(newOrder)}
            renderItem={(node) => <Item item={node.data as Widget} />}
          />
        </Box>
      </ModalContent>
      <ModalActions>
        <Button key="cancel" onClick={handleClose}>
          {t("general.cancel")}
        </Button>
        <Button key="save" onClick={handleSave}>
          {t("general.save")}
        </Button>
      </ModalActions>
    </Modal>
  );
};

export default React.forwardRef(WidgetOrderDialog);
